import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {Subscription} from 'rxjs';
import {OfferRuleModel} from '../../../../../model/offer-rule.model';
import {OfferRuleTypeEnumerator} from '../enumerators/offer-rule-type.enumerator';
import {RulePrivacyEnumerator} from '../enumerators/rule-privacy.enumerator';
import {VehicleTypeEnumerator} from '../enumerators/vehicle-type.enumerator';
import {EnhancedRuleDraft} from '../models/enhanced-rule-draft.model';
import {GeneratedOfferSetDraft} from '../models/generated-offer-set-draft.model';
import {
  QualifyingRuleSourceAprOffer,
  QualifyingRuleSourceDetail,
  QualifyingRuleSourceFinanceOffer,
  QualifyingRuleSourceLeaseOffer,
  QualifyingRuleSourceOffers,
  QualifyingRuleSourceVehicle
} from '../models/qualifying-rule-source-detail.model';
import {
  QualifyingRuleSourceFilters,
  QualifyingRuleSourceRow
} from '../models/qualifying-rule-source.model';
import {
  anyTrimValue,
  IpopRuleWorkflowRuleDetailsValue,
  IpopRuleWorkflowRuleStatus
} from '../rule-details/ipop-rule-workflow-rule-details.component';
import {QualifyingRuleSourceService} from '../services/qualifying-rule-source.service';
import {
  cloneEnhancedRuleDraft,
  cloneGeneratedOfferSetDraft,
  cloneOfferRule,
  cloneQualifyingRuleSource,
  normalizeEnhancedRuleDraft,
  normalizeDollarDiscountRuleDraft
} from '../utilities/offer-rule-workflow.mapper';

export type IpopRuleWorkflowModalMode = 'create' | 'edit';

enum RuleWorkflowStep {
  RuleDetails = 1,
  OfferSetup = 2
}

enum DetailRequestPurpose {
  Continue = 'continue'
}

interface DetailRequestSnapshot {
  readonly requestId: number;
  readonly initializationId: number;
  readonly accountId: number;
  readonly offerSetId: number;
  readonly filters: QualifyingRuleSourceFilters;
  readonly purpose: DetailRequestPurpose;
}

const createMode: IpopRuleWorkflowModalMode = 'create';
const editMode: IpopRuleWorkflowModalMode = 'edit';
const activeStatus: IpopRuleWorkflowRuleStatus = 'Active';
const firstPage = 1;
const discoveryPageSize = 5;
const defaultMaximumPriceSortDirection = 'asc' as const;
const invalidIdentifier = 0;
const focusDelayMilliseconds = 0;
const genericInitializationError = 'This rule could not be initialized. Cancel and try again.';
const invalidAccountError = 'A valid account is required before this rule can continue or stage.';
const contradictoryRuleError = 'This persisted rule has contradictory or incomplete rule type data and cannot be staged.';
const invalidDraftError = 'The staged rule data is inconsistent. The persisted rule was used when available.';
const detailLoadError = 'The selected source is unavailable. Retry or select another eligible source.';
const invalidOfferStageError = 'The rule or Offer Setup changed and is not ready to stage.';
const invalidDiscountStageError = 'Complete the Dollar Discount Rule before staging.';
const dollarDiscountRequiredMessage = 'Please at least include a Maximum Price and Dollar Discount Amount';

@Component({
  selector: 'auto-ipop-rule-workflow-rule-modal',
  templateUrl: './ipop-rule-workflow-rule-modal.component.html',
  styleUrls: ['./ipop-rule-workflow-rule-modal.component.scss'],
  standalone: false
})
export class IpopRuleWorkflowRuleModalComponent implements OnChanges, OnDestroy
{
  @Input() visible = false;
  @Input() dialogTitle = '';
  @Input() mode: IpopRuleWorkflowModalMode = createMode;
  @Input() accountId: number | null = null;
  @Input() rule: OfferRuleModel | null = null;
  @Input() existingDraft: EnhancedRuleDraft | null = null;

  @Output() closed = new EventEmitter<void>();
  @Output() staged = new EventEmitter<EnhancedRuleDraft>();

  @ViewChild('stepOneHeading') private stepOneHeading: ElementRef<HTMLElement> | undefined;
  @ViewChild('stepTwoHeading') private stepTwoHeading: ElementRef<HTMLElement> | undefined;
  @ViewChild('detailErrorRegion') private detailErrorRegion: ElementRef<HTMLElement> | undefined;

  readonly steps = RuleWorkflowStep;
  readonly dialogStyle = {width: 'min(1200px, 96vw)'};
  readonly dialogContentStyle = {maxHeight: '82vh', overflow: 'auto'};
  readonly dollarDiscountRequiredMessage = dollarDiscountRequiredMessage;

  activeStep = RuleWorkflowStep.RuleDetails;
  resolvedDialogTitle = 'Create Rule';
  initialRule: OfferRuleModel | null = null;
  initialRuleType = OfferRuleTypeEnumerator.OfferSetRule;
  initialDiscoveryFilters: QualifyingRuleSourceFilters | null = null;
  selectedSource: QualifyingRuleSourceRow | null = null;
  offerSetupSource: QualifyingRuleSourceRow | null = null;
  sourceDetail: QualifyingRuleSourceDetail | null = null;
  generatedOfferSetDraft: GeneratedOfferSetDraft | null = null;
  currentRuleDetails: IpopRuleWorkflowRuleDetailsValue | null = null;
  currentDiscoveryFilters: QualifyingRuleSourceFilters | null = null;
  qualifyingVehicleCount = 0;
  ruleDetailsReady = false;
  offerSetupReady = false;
  detailLoading = false;
  initializationError = '';
  detailError = '';
  stageError = '';
  liveAnnouncement = '';

  private baseRule: OfferRuleModel | null = null;
  private detailSubscription: Subscription | null = null;
  private initializationId = 0;
  private requestId = 0;
  private closeEmitted = false;
  private stageEmitted = false;

  constructor(private readonly qualifyingRuleSourceService: QualifyingRuleSourceService)
  {
  }

  ngOnChanges(changes: SimpleChanges): void
  {
    const shouldInitialize = this.visible && this.hasInitializationChange(changes);
    if (shouldInitialize) {
      this.initializeModal();
    }
    if (this.visible && !shouldInitialize && changes['dialogTitle'] !== undefined) {
      this.resolvedDialogTitle = this.resolveDialogTitle();
    }
    if (changes['visible'] !== undefined && !this.visible) {
      this.handleModalHidden();
    }
  }

  ngOnDestroy(): void
  {
    this.invalidateDetailRequest();
  }

  onRuleDetailsChange(ruleDetails: IpopRuleWorkflowRuleDetailsValue): void
  {
    const previousRuleType = this.currentRuleDetails?.ruleType ?? this.initialRuleType;
    this.currentRuleDetails = this.copyRuleDetails(ruleDetails);
    this.stageError = '';
    if (previousRuleType !== ruleDetails.ruleType) {
      this.handleRuleTypeTransition(ruleDetails.ruleType);
    }
  }

  onDiscoveryFiltersChange(filters: QualifyingRuleSourceFilters | null): void
  {
    const filtersChanged = this.selectedSource !== null
      && this.currentDiscoveryFilters !== null
      && (filters === null || !this.eligibilityFiltersMatch(this.currentDiscoveryFilters, filters));
    this.currentDiscoveryFilters = this.copyOptionalFilters(filters);
    if (filtersChanged
      && this.mode !== editMode
      && this.activeStep === RuleWorkflowStep.RuleDetails
      && !this.hasInitializedCreateOfferSetup()) {
      this.invalidateSelectedSource();
    }
  }

  onSelectedSourceChange(source: QualifyingRuleSourceRow | null): void
  {
    const currentSourceId = this.selectedSource?.offerSetId ?? null;
    const nextSourceId = source?.offerSetId ?? null;
    if (this.mode !== editMode && source === null && this.selectedSource !== null) {
      this.invalidateSelectedSource();
    }
    if (this.mode !== editMode && source !== null && currentSourceId !== nextSourceId) {
      this.replaceSelectedSource(source);
    }
  }

  onSourceInvalidated(): void
  {
    if (this.mode !== editMode) {
      this.invalidateSelectedSource();
    }
  }

  onRuleDetailsReadinessChange(readiness: boolean): void
  {
    this.ruleDetailsReady = readiness;
  }

  onQualifyingVehicleCountChange(count: number): void
  {
    let qualifyingVehicleCount = 0;
    if (Number.isSafeInteger(count) && count > 0) {
      qualifyingVehicleCount = count;
    }
    this.qualifyingVehicleCount = qualifyingVehicleCount;
  }

  onGeneratedDraftChange(draft: GeneratedOfferSetDraft | null): void
  {
    this.generatedOfferSetDraft = this.copyOptionalGeneratedDraft(draft);
    this.stageError = '';
  }

  onOfferSetupReadinessChange(readiness: boolean): void
  {
    this.offerSetupReady = readiness;
  }

  onContinue(): void
  {
    this.stageError = '';
    if (this.mode === editMode) {
      this.continueEditRule();
    }
    if (this.mode !== editMode) {
      this.continueCreateRule();
    }
  }

  onRetryDetail(): void
  {
    this.stageError = '';
    this.onContinue();
  }

  onBackRequested(): void
  {
    this.activeStep = RuleWorkflowStep.RuleDetails;
    this.offerSetupReady = false;
    this.stageError = '';
    this.liveAnnouncement = 'Step 1 of 2: Rule Details.';
    this.focusHeading(RuleWorkflowStep.RuleDetails);
  }

  onOfferStageRequested(draft: GeneratedOfferSetDraft): void
  {
    this.stageError = '';
    const source = this.offerSetupSource;
    const ruleDetails = this.currentRuleDetails;
    if (this.canStageOfferRule(draft) && ruleDetails !== null) {
      const envelope = this.buildOfferRuleEnvelope(draft, ruleDetails, source);
      this.emitStagedEnvelope(envelope);
    } else {
      this.stageError = invalidOfferStageError;
    }
  }

  onDollarDiscountStage(): void
  {
    this.stageError = '';
    const ruleDetails = this.currentRuleDetails;
    if (this.canStageDollarDiscountRule() && ruleDetails !== null) {
      const envelope = this.buildDollarDiscountEnvelope(ruleDetails);
      this.emitStagedEnvelope(envelope);
    } else {
      this.stageError = invalidDiscountStageError;
    }
  }

  requestClose(): void
  {
    if (!this.closeEmitted && !this.stageEmitted) {
      this.closeEmitted = true;
      this.invalidateDetailRequest();
      this.closed.emit();
    }
  }

  onDialogHide(): void
  {
    this.requestClose();
  }

  isOfferSetRule(): boolean
  {
    const isOfferSet = this.resolveCurrentRuleType() === OfferRuleTypeEnumerator.OfferSetRule;

    return isOfferSet;
  }

  isEditMode(): boolean
  {
    const editing = this.mode === editMode;

    return editing;
  }

  canContinue(): boolean
  {
    const baseReady = this.visible
      && this.initializationError === ''
      && !this.detailLoading
      && this.ruleDetailsReady
      && this.isOfferSetRule()
      && this.isValidAccountId(this.accountId)
      && this.currentRuleDetails !== null
      && this.currentDiscoveryFilters !== null;
    let sourceReady = this.hasCreateSourceContext();
    if (this.mode === editMode) {
      sourceReady = this.hasExistingOfferSetupContext();
    }
    const ready = baseReady && sourceReady;

    return ready;
  }

  continueGuidanceMessage(): string
  {
    let message = 'Please select a qualifying vehicle to continue.';
    const details = this.currentRuleDetails;
    if (details === null
      || details.year === null
      || this.normalizeText(details.make) === null
      || this.normalizeText(details.model) === null
      || this.normalizeText(details.trim) === null) {
      message = 'Please select a Year, Make, Model, and Trim to continue.';
    }
    if (details !== null
      && details.year !== null
      && this.normalizeText(details.make) !== null
      && this.normalizeText(details.model) !== null
      && this.normalizeText(details.trim) !== null
      && details.maximumPrice === null) {
      message = 'Please enter a Maximum Price to continue.';
    }

    return message;
  }

  canStageDollarDiscountRule(): boolean
  {
    const ready = this.visible
      && this.initializationError === ''
      && !this.stageEmitted
      && this.ruleDetailsReady
      && this.resolveCurrentRuleType() === OfferRuleTypeEnumerator.DollarDiscountRule
      && this.isValidAccountId(this.accountId)
      && this.currentRuleDetails !== null;

    return ready;
  }

  private initializeModal(): void
  {
    this.initializationId += 1;
    this.invalidateDetailRequest();
    this.resetModalState();
    this.resolvedDialogTitle = this.resolveDialogTitle();
    if (!this.isValidAccountId(this.accountId)) {
      this.initializationError = invalidAccountError;
    }
    if (this.mode === editMode) {
      this.initializeEditMode();
    }
    if (this.mode !== editMode) {
      this.initializeCreateMode();
    }
  }

  private resetModalState(): void
  {
    this.activeStep = RuleWorkflowStep.RuleDetails;
    this.initialRule = null;
    this.baseRule = null;
    this.initialRuleType = OfferRuleTypeEnumerator.OfferSetRule;
    this.initialDiscoveryFilters = null;
    this.currentDiscoveryFilters = null;
    this.currentRuleDetails = null;
    this.qualifyingVehicleCount = 0;
    this.selectedSource = null;
    this.offerSetupSource = null;
    this.sourceDetail = null;
    this.generatedOfferSetDraft = null;
    this.ruleDetailsReady = false;
    this.offerSetupReady = false;
    this.detailLoading = false;
    this.initializationError = '';
    this.detailError = '';
    this.stageError = '';
    this.liveAnnouncement = 'Step 1: Rule Details.';
    this.closeEmitted = false;
    this.stageEmitted = false;
  }

  private initializeCreateMode(): void
  {
    const rule = this.buildFreshRule();
    this.baseRule = cloneOfferRule(rule);
    this.initialRule = cloneOfferRule(rule);
    this.initialRuleType = OfferRuleTypeEnumerator.OfferSetRule;
  }

  private initializeEditMode(): void
  {
    let initialized = false;
    if (this.existingDraft !== null && this.isValidExistingDraft(this.existingDraft)) {
      const draft = cloneEnhancedRuleDraft(this.existingDraft);
      this.initializeFromExistingDraft(draft);
      initialized = true;
    }
    if (!initialized) {
      this.initializeCreateMode();
      this.initializationError = genericInitializationError;
    }
    if (this.existingDraft !== null && !this.isValidExistingDraft(this.existingDraft)) {
      this.stageError = invalidDraftError;
    }
  }

  private initializeFromExistingDraft(draft: EnhancedRuleDraft): void
  {
    const rule = this.sanitizeInputRule(draft.rule);
    this.baseRule = cloneOfferRule(rule);
    this.initialRule = cloneOfferRule(rule);
    this.initialRuleType = draft.ruleType;
    if (draft.ruleType === OfferRuleTypeEnumerator.OfferSetRule) {
      this.initializeExistingOfferDraft(draft);
    }
    if (draft.ruleType === OfferRuleTypeEnumerator.DollarDiscountRule) {
      this.initializeExistingDollarDraft(draft);
    }
  }

  private initializeExistingOfferDraft(draft: EnhancedRuleDraft): void
  {
    const source = draft.source;
    const generatedDraft = draft.generatedOfferSet;
    const baseRule = this.baseRule;
    if (generatedDraft !== null && baseRule !== null) {
      this.applyExistingOfferDraft(baseRule, source, generatedDraft);
    }
    if (generatedDraft === null || baseRule === null) {
      this.initializationError = genericInitializationError;
      this.invalidateSelectedSource();
    }
  }

  private applyExistingOfferDraft(
    rule: OfferRuleModel,
    source: QualifyingRuleSourceRow | null,
    generatedDraft: GeneratedOfferSetDraft
  ): void
  {
    const filters = this.buildFiltersFromRule(rule, source);
    if (filters !== null) {
      this.initialDiscoveryFilters = this.copyFilters(filters);
      this.currentDiscoveryFilters = this.copyFilters(filters);
      this.offerSetupSource = null;
      if (source !== null) {
        this.offerSetupSource = cloneQualifyingRuleSource(source);
      }
      this.generatedOfferSetDraft = cloneGeneratedOfferSetDraft(generatedDraft);
    }
    if (filters === null) {
      this.initializationError = genericInitializationError;
      this.invalidateSelectedSource();
    }
  }

  private initializeExistingDollarDraft(draft: EnhancedRuleDraft): void
  {
    const normalizedDraft = normalizeDollarDiscountRuleDraft(draft);
    const rule = this.sanitizeInputRule(normalizedDraft.rule);
    this.baseRule = cloneOfferRule(rule);
    this.initialRule = cloneOfferRule(rule);
    this.selectedSource = null;
    this.offerSetupSource = null;
    this.sourceDetail = null;
    this.generatedOfferSetDraft = null;
  }


  private requestSourceDetail(
    offerSetId: number,
    filters: QualifyingRuleSourceFilters,
    purpose: DetailRequestPurpose
  ): void
  {
    this.invalidateDetailRequest();
    const canLoadCreateSource = this.mode === createMode
      && this.isValidAccountId(this.accountId)
      && this.isValidIdentifier(offerSetId);
    if (canLoadCreateSource) {
      const snapshot = this.buildRequestSnapshot(offerSetId, filters, purpose);
      this.detailLoading = true;
      this.detailError = '';
      this.liveAnnouncement = this.resolveDetailLoadingMessage(purpose);
      this.detailSubscription = this.qualifyingRuleSourceService
        .getQualifyingRuleSourceDetail(snapshot.accountId, snapshot.offerSetId, snapshot.filters)
        .subscribe({
          next: (detail) => this.applyDetailResponse(detail, snapshot),
          error: (error: unknown) => this.applyDetailFailure(error, snapshot)
        });
    }
  }

  private buildRequestSnapshot(
    offerSetId: number,
    filters: QualifyingRuleSourceFilters,
    purpose: DetailRequestPurpose
  ): DetailRequestSnapshot
  {
    this.requestId += 1;
    const snapshot: DetailRequestSnapshot = {
      requestId: this.requestId,
      initializationId: this.initializationId,
      accountId: this.resolveAccountId(),
      offerSetId,
      filters: this.copyFilters(filters),
      purpose
    };

    return snapshot;
  }

  private applyDetailResponse(detail: QualifyingRuleSourceDetail, snapshot: DetailRequestSnapshot): void
  {
    if (this.isCurrentDetailResponse(snapshot) && this.detailIdentitiesMatch(detail, snapshot)) {
      const detailCopy = this.copySourceDetail(detail);
      this.detailLoading = false;
      this.detailError = '';
      if (snapshot.purpose === DetailRequestPurpose.Continue) {
        this.applyContinueDetail(detailCopy);
      }
    }
    if (this.isCurrentDetailResponse(snapshot) && !this.detailIdentitiesMatch(detail, snapshot)) {
      this.applyUnavailableDetail(snapshot);
    }
  }

  private applyDetailFailure(error: unknown, snapshot: DetailRequestSnapshot): void
  {
    if (this.isCurrentDetailResponse(snapshot)) {
      this.detailLoading = false;
      const errorMessage = this.resolveDetailLoadError(snapshot.purpose);
      this.detailError = errorMessage;
      this.liveAnnouncement = errorMessage;
      this.focusDetailError();
    }
    void error;
  }


  private applyContinueDetail(detail: QualifyingRuleSourceDetail): void
  {
    this.sourceDetail = this.copySourceDetail(detail);
    this.applyOfferSetupStep();
  }

  private applyUnavailableDetail(snapshot: DetailRequestSnapshot): void
  {
    this.detailLoading = false;
    this.clearSourceDerivedState();
    if (snapshot.purpose === DetailRequestPurpose.Continue) {
      this.selectedSource = null;
    }
    const errorMessage = this.resolveDetailLoadError(snapshot.purpose);
    this.detailError = errorMessage;
    this.liveAnnouncement = errorMessage;
    this.focusDetailError();
  }

  private isCurrentDetailResponse(snapshot: DetailRequestSnapshot): boolean
  {
    let current = this.visible
      && snapshot.requestId === this.requestId
      && snapshot.initializationId === this.initializationId
      && this.isOfferSetRule()
      && this.accountId === snapshot.accountId;
    if (current && snapshot.purpose === DetailRequestPurpose.Continue) {
      current = this.currentDiscoveryFilters !== null
        && this.eligibilityFiltersMatch(this.currentDiscoveryFilters, snapshot.filters)
        && this.selectedSource?.offerSetId === snapshot.offerSetId;
    }
    return current;
  }

  private detailIdentitiesMatch(detail: QualifyingRuleSourceDetail, snapshot: DetailRequestSnapshot): boolean
  {
    const matches = detail.accountId === snapshot.accountId && detail.offerSetId === snapshot.offerSetId;

    return matches;
  }

  private handleRuleTypeTransition(ruleType: OfferRuleTypeEnumerator): void
  {
    if (this.currentRuleDetails !== null) {
      const transitionRule = this.buildRule(this.currentRuleDetails, null);
      this.initialRule = cloneOfferRule(transitionRule);
    }
    this.invalidateSelectedSource();
    this.initialRuleType = ruleType;
    if (ruleType === OfferRuleTypeEnumerator.DollarDiscountRule) {
      this.liveAnnouncement = 'Dollar Discount Rule selected. Offer source and setup state were cleared.';
    }
    if (ruleType === OfferRuleTypeEnumerator.OfferSetRule) {
      this.liveAnnouncement = 'Offer Set Rule selected. Select a new qualifying source.';
    }
  }

  private replaceSelectedSource(source: QualifyingRuleSourceRow): void
  {
    this.invalidateDetailRequest();
    this.clearSourceDerivedState();
    this.selectedSource = cloneQualifyingRuleSource(source);
    this.offerSetupSource = cloneQualifyingRuleSource(source);
    this.detailError = '';
  }

  private invalidateSelectedSource(): void
  {
    this.invalidateDetailRequest();
    this.selectedSource = null;
    this.offerSetupSource = null;
    this.clearSourceDerivedState();
  }

  private clearSourceDerivedState(): void
  {
    this.sourceDetail = null;
    this.generatedOfferSetDraft = null;
    this.offerSetupReady = false;
    this.detailError = '';
    this.activeStep = RuleWorkflowStep.RuleDetails;
  }

  private invalidateDetailRequest(): void
  {
    this.requestId += 1;
    if (this.detailSubscription !== null) {
      this.detailSubscription.unsubscribe();
      this.detailSubscription = null;
    }
    this.detailLoading = false;
  }

  private canStageOfferRule(draft: GeneratedOfferSetDraft): boolean
  {
    const baseReady = this.visible
      && !this.stageEmitted
      && !this.detailLoading
      && this.initializationError === ''
      && this.isOfferSetRule()
      && this.ruleDetailsReady
      && this.offerSetupReady
      && this.currentRuleDetails !== null
      && this.isValidAccountId(this.accountId)
      && draft !== null;
    let sourceReady = this.hasCreateSourceContext() && this.hasMatchingSourceDetail();
    if (this.mode === editMode) {
      sourceReady = this.hasExistingOfferSetupContext();
    }
    const ready = baseReady && sourceReady;

    return ready;
  }

  private continueCreateRule(): void
  {
    const source = this.selectedSource;
    const filters = this.currentDiscoveryFilters;
    const ruleDetails = this.currentRuleDetails;
    if (this.canContinue() && source !== null && filters !== null && ruleDetails !== null) {
      const sourceCopy = cloneQualifyingRuleSource(source);
      const currentRule = this.buildRule(ruleDetails, sourceCopy.offerSetId);
      this.baseRule = cloneOfferRule(currentRule);
      const initializedOfferSetup = this.hasInitializedCreateOfferSetup();
      if (initializedOfferSetup) {
        this.applyOfferSetupStep();
      }
      if (!initializedOfferSetup) {
        this.requestSourceDetail(sourceCopy.offerSetId, this.copyFilters(filters), DetailRequestPurpose.Continue);
      }
    }
  }

  private continueEditRule(): void
  {
    const ruleDetails = this.currentRuleDetails;
    const offerSetId = this.baseRule?.offerSetId ?? invalidIdentifier;
    if (this.canContinue() && ruleDetails !== null && this.isValidIdentifier(offerSetId)) {
      this.baseRule = cloneOfferRule(this.buildRule(ruleDetails, offerSetId));
      this.applyOfferSetupStep();
    }
  }

  private applyOfferSetupStep(): void
  {
    this.activeStep = RuleWorkflowStep.OfferSetup;
    this.offerSetupReady = false;
    this.liveAnnouncement = 'Step 2 of 2: Offer Setup.';
    this.focusHeading(RuleWorkflowStep.OfferSetup);
  }

  private hasCreateSourceContext(): boolean
  {
    const sourceId = this.selectedSource?.offerSetId ?? invalidIdentifier;
    const ready = this.selectedSource !== null
      && this.selectedSource.selectable
      && this.isValidIdentifier(sourceId);

    return ready;
  }

  private hasInitializedCreateOfferSetup(): boolean
  {
    const initialized = this.mode !== editMode
      && this.hasExistingOfferSetupContext()
      && this.hasMatchingSourceDetail();

    return initialized;
  }

  private hasExistingOfferSetupContext(): boolean
  {
    const offerSetId = this.baseRule?.offerSetId ?? invalidIdentifier;
    const hasOfferValues = this.generatedOfferSetDraft !== null || this.sourceDetail !== null;
    const ready = this.isValidIdentifier(offerSetId) && hasOfferValues;

    return ready;
  }

  private hasMatchingSourceDetail(): boolean
  {
    const sourceId = this.offerSetupSource?.offerSetId ?? invalidIdentifier;
    const matches = this.sourceDetail !== null
      && this.sourceDetail.accountId === this.accountId
      && this.sourceDetail.offerSetId === sourceId;

    return matches;
  }

  private resolveDetailLoadingMessage(purpose: DetailRequestPurpose): string
  {
    void purpose;
    const message = 'Loading selected source details.';

    return message;
  }

  private resolveDetailLoadError(purpose: DetailRequestPurpose): string
  {
    void purpose;
    const errorMessage = detailLoadError;

    return errorMessage;
  }

  private buildOfferRuleEnvelope(
    draft: GeneratedOfferSetDraft,
    ruleDetails: IpopRuleWorkflowRuleDetailsValue,
    selectedSource: QualifyingRuleSourceRow | null
  ): EnhancedRuleDraft
  {
    let source: QualifyingRuleSourceRow | null = null;
    let offerSetId = this.baseRule?.offerSetId ?? null;
    if (selectedSource !== null) {
      source = cloneQualifyingRuleSource(selectedSource);
      offerSetId = source.offerSetId;
    }
    const rule = this.buildRule(ruleDetails, offerSetId);
    const envelope: EnhancedRuleDraft = {
      ruleType: OfferRuleTypeEnumerator.OfferSetRule,
      rule,
      source,
      generatedOfferSet: cloneGeneratedOfferSetDraft(draft),
      serverMetadata: this.getExistingServerMetadata()
    };
    const normalizedEnvelope = normalizeEnhancedRuleDraft(envelope);

    return normalizedEnvelope;
  }

  private buildDollarDiscountEnvelope(ruleDetails: IpopRuleWorkflowRuleDetailsValue): EnhancedRuleDraft
  {
    const rule = this.buildRule(ruleDetails, null);
    const envelope: EnhancedRuleDraft = {
      ruleType: OfferRuleTypeEnumerator.DollarDiscountRule,
      rule,
      source: null,
      generatedOfferSet: null,
      serverMetadata: this.getExistingServerMetadata()
    };
    const normalizedEnvelope = normalizeDollarDiscountRuleDraft(envelope);

    return normalizedEnvelope;
  }

  private getExistingServerMetadata(): EnhancedRuleDraft['serverMetadata']
  {
    const metadata = this.existingDraft?.serverMetadata ?? null;
    let copy: EnhancedRuleDraft['serverMetadata'] = null;
    if (metadata !== null) {
      copy = {
        ruleId: metadata.ruleId,
        version: metadata.version,
        generatedOfferSetId: metadata.generatedOfferSetId
      };
    }

    return copy;
  }

  private emitStagedEnvelope(envelope: EnhancedRuleDraft): void
  {
    if (!this.stageEmitted) {
      const output = cloneEnhancedRuleDraft(normalizeEnhancedRuleDraft(envelope));
      this.stageEmitted = true;
      this.staged.emit(output);
    }
  }

  private buildRule(
    details: IpopRuleWorkflowRuleDetailsValue,
    offerSetId: number | null
  ): OfferRuleModel
  {
    const rule = this.buildRuleIdentity();
    this.applyRuleCriteria(rule, details);
    rule.vehicleCondition = this.mapVehicleType(details.vehicleType);
    rule.isPrivate = details.ruleType === OfferRuleTypeEnumerator.DollarDiscountRule
      || details.privacy === RulePrivacyEnumerator.Private;
    rule.status = details.status;
    if (details.ruleType === OfferRuleTypeEnumerator.OfferSetRule && offerSetId !== null) {
      rule.offerSetId = offerSetId;
    }
    if (details.ruleType === OfferRuleTypeEnumerator.DollarDiscountRule && details.dollarDiscount !== null) {
      rule.dollarAmountDiscount = details.dollarDiscount;
    }

    return rule;
  }

  private buildRuleIdentity(): OfferRuleModel
  {
    const rule: OfferRuleModel = {accountId: this.resolveAccountId()};
    if (this.baseRule?.id !== undefined) {
      rule.id = this.baseRule.id;
    }
    if (this.baseRule?.created !== undefined) {
      rule.created = this.baseRule.created;
    }
    if (this.baseRule?.modified !== undefined) {
      rule.modified = this.baseRule.modified;
    }

    return rule;
  }

  private applyRuleCriteria(rule: OfferRuleModel, details: IpopRuleWorkflowRuleDetailsValue): void
  {
    if (details.year !== null) {
      rule.year = details.year;
    }
    this.applyOptionalText(rule, 'make', details.make);
    this.applyOptionalText(rule, 'model', details.model);
    if (details.trim !== anyTrimValue) {
      this.applyOptionalText(rule, 'trim', details.trim);
    }
    if (details.maximumPrice !== null) {
      rule.maxSalePrice = details.maximumPrice;
    }
    if (details.vehicleLimit !== null) {
      rule.vehicleLimit = details.vehicleLimit;
    }
  }

  private applyOptionalText(
    rule: OfferRuleModel,
    field: 'make' | 'model' | 'trim',
    value: string | null
  ): void
  {
    const normalizedValue = this.normalizeText(value);
    if (normalizedValue !== null) {
      rule[field] = normalizedValue;
    }
  }

  private buildFreshRule(): OfferRuleModel
  {
    const rule: OfferRuleModel = {
      accountId: this.resolveAccountId(),
      vehicleCondition: this.mapVehicleType(VehicleTypeEnumerator.Any),
      isPrivate: true,
      status: activeStatus
    };

    return rule;
  }

  private sanitizeInputRule(rule: OfferRuleModel): OfferRuleModel
  {
    const copy = cloneOfferRule(rule);
    copy.accountId = this.resolveAccountId();

    return copy;
  }

  private buildFiltersFromRule(
    rule: OfferRuleModel,
    source: QualifyingRuleSourceRow | null
  ): QualifyingRuleSourceFilters | null
  {
    let filters: QualifyingRuleSourceFilters | null = null;
    const vehicleType = this.resolveVehicleType(rule.vehicleCondition);
    if (vehicleType !== null) {
      filters = {
        year: rule.year ?? null,
        make: this.normalizeText(rule.make ?? null),
        model: this.normalizeText(rule.model ?? null),
        trim: this.normalizeText(rule.trim ?? null),
        maximumPrice: rule.maxSalePrice ?? null,
        vehicleType,
        privacy: this.resolvePrivacy(rule.isPrivate),
        maximumPriceSortDirection: defaultMaximumPriceSortDirection,
        page: firstPage,
        pageSize: discoveryPageSize
      };
    }

    return filters;
  }
  private isValidExistingDraft(draft: EnhancedRuleDraft): boolean
  {
    let valid = false;
    if (draft.ruleType === OfferRuleTypeEnumerator.OfferSetRule) {
      valid = this.isValidOfferDraft(draft);
    }
    if (draft.ruleType === OfferRuleTypeEnumerator.DollarDiscountRule) {
      valid = this.isValidDollarDraft(draft);
    }

    return valid;
  }

  private isValidOfferDraft(draft: EnhancedRuleDraft): boolean
  {
    const ruleSourceId = draft.rule.offerSetId ?? invalidIdentifier;
    const sourceId = draft.source?.offerSetId ?? invalidIdentifier;
    const generatedOfferSetId = draft.serverMetadata?.generatedOfferSetId ?? invalidIdentifier;
    const discount = draft.rule.dollarAmountDiscount ?? 0;
    const hasDiscount = Number.isFinite(discount) && discount > 0;
    const validCreate = draft.serverMetadata === null
      && this.isValidIdentifier(ruleSourceId)
      && sourceId === ruleSourceId
      && draft.source !== null
      && draft.source.selectable;
    const validPersisted = this.isValidServerMetadata(draft)
      && ruleSourceId === generatedOfferSetId
      && draft.source === null;
    const valid = (validCreate || validPersisted)
      && draft.generatedOfferSet !== null
      && !hasDiscount;

    return valid;
  }

  private isValidDollarDraft(draft: EnhancedRuleDraft): boolean
  {
    const discount = draft.rule.dollarAmountDiscount ?? 0;
    const offerSetId = draft.rule.offerSetId ?? invalidIdentifier;
    const valid = Number.isFinite(discount)
      && discount > 0
      && !this.isValidIdentifier(offerSetId)
      && draft.source === null
      && draft.generatedOfferSet === null
      && draft.rule.isPrivate !== false;

    return valid;
  }

  private isValidServerMetadata(draft: EnhancedRuleDraft): boolean
  {
    const metadata = draft.serverMetadata;
    const valid = metadata !== null
      && this.isValidIdentifier(metadata.ruleId)
      && metadata.version.trim() !== ''
      && this.isValidIdentifier(metadata.generatedOfferSetId ?? invalidIdentifier);

    return valid;
  }

  private resolveCurrentRuleType(): OfferRuleTypeEnumerator
  {
    const ruleType = this.currentRuleDetails?.ruleType ?? this.initialRuleType;

    return ruleType;
  }

  private resolveDialogTitle(): string
  {
    let title = 'Create Rule';
    if (this.mode === editMode) {
      title = 'Edit Rule';
    }

    return title;
  }

  formatPrivacy(privacy: RulePrivacyEnumerator): string
  {
    const labels: Record<RulePrivacyEnumerator, string> = {
      [RulePrivacyEnumerator.Private]: 'Private',
      [RulePrivacyEnumerator.Public]: 'Public'
    };
    const label = labels[privacy];

    return label;
  }

  formatVehicleType(vehicleType: VehicleTypeEnumerator): string
  {
    const labels: Record<VehicleTypeEnumerator, string> = {
      [VehicleTypeEnumerator.Any]: 'Any',
      [VehicleTypeEnumerator.New]: 'New',
      [VehicleTypeEnumerator.Used]: 'Used',
      [VehicleTypeEnumerator.Cpo]: 'CPO'
    };
    const label = labels[vehicleType];

    return label;
  }

  private resolveVehicleType(vehicleCondition: string | undefined): VehicleTypeEnumerator | null
  {
    let vehicleType: VehicleTypeEnumerator | null = null;
    if (vehicleCondition === undefined || vehicleCondition === 'Any') {
      vehicleType = VehicleTypeEnumerator.Any;
    }
    if (vehicleCondition === 'New') {
      vehicleType = VehicleTypeEnumerator.New;
    }
    if (vehicleCondition === 'Used') {
      vehicleType = VehicleTypeEnumerator.Used;
    }
    if (vehicleCondition === 'CPO') {
      vehicleType = VehicleTypeEnumerator.Cpo;
    }

    return vehicleType;
  }

  private mapVehicleType(vehicleType: VehicleTypeEnumerator): string
  {
    const values: Record<VehicleTypeEnumerator, string> = {
      [VehicleTypeEnumerator.Any]: 'Any',
      [VehicleTypeEnumerator.New]: 'New',
      [VehicleTypeEnumerator.Used]: 'Used',
      [VehicleTypeEnumerator.Cpo]: 'CPO'
    };
    const value = values[vehicleType];

    return value;
  }

  private resolvePrivacy(isPrivate: boolean | undefined): RulePrivacyEnumerator
  {
    let privacy = RulePrivacyEnumerator.Private;
    if (isPrivate === false) {
      privacy = RulePrivacyEnumerator.Public;
    }

    return privacy;
  }

  private resolveAccountId(): number
  {
    let accountId = invalidIdentifier;
    if (this.isValidAccountId(this.accountId)) {
      accountId = this.accountId;
    }

    return accountId;
  }

  private isValidAccountId(accountId: number | null): accountId is number
  {
    const valid = accountId !== null && this.isValidIdentifier(accountId);

    return valid;
  }

  private isValidIdentifier(identifier: number): boolean
  {
    const valid = Number.isSafeInteger(identifier) && identifier > 0;

    return valid;
  }

  private eligibilityFiltersMatch(
    first: QualifyingRuleSourceFilters,
    second: QualifyingRuleSourceFilters
  ): boolean
  {
    const matches = first.year === second.year
      && first.make === second.make
      && first.model === second.model
      && first.trim === second.trim
      && first.maximumPrice === second.maximumPrice
      && first.vehicleType === second.vehicleType
      && first.privacy === second.privacy;

    return matches;
  }

  private copyFilters(filters: QualifyingRuleSourceFilters): QualifyingRuleSourceFilters
  {
    const copy: QualifyingRuleSourceFilters = {
      year: filters.year,
      make: filters.make,
      model: filters.model,
      trim: filters.trim,
      maximumPrice: filters.maximumPrice,
      vehicleType: filters.vehicleType,
      privacy: filters.privacy,
      maximumPriceSortDirection: filters.maximumPriceSortDirection,
      page: filters.page,
      pageSize: filters.pageSize
    };

    return copy;
  }

  private copyOptionalFilters(filters: QualifyingRuleSourceFilters | null): QualifyingRuleSourceFilters | null
  {
    let copy: QualifyingRuleSourceFilters | null = null;
    if (filters !== null) {
      copy = this.copyFilters(filters);
    }

    return copy;
  }

  private copyRuleDetails(details: IpopRuleWorkflowRuleDetailsValue): IpopRuleWorkflowRuleDetailsValue
  {
    const copy: IpopRuleWorkflowRuleDetailsValue = {
      ruleType: details.ruleType,
      privacy: details.privacy,
      status: details.status,
      vehicleType: details.vehicleType,
      year: details.year,
      make: details.make,
      model: details.model,
      trim: details.trim,
      maximumPrice: details.maximumPrice,
      vehicleLimit: details.vehicleLimit,
      dollarDiscount: details.dollarDiscount
    };

    return copy;
  }

  private copyOptionalGeneratedDraft(draft: GeneratedOfferSetDraft | null): GeneratedOfferSetDraft | null
  {
    let copy: GeneratedOfferSetDraft | null = null;
    if (draft !== null) {
      copy = cloneGeneratedOfferSetDraft(draft);
    }

    return copy;
  }

  private copySourceDetail(detail: QualifyingRuleSourceDetail): QualifyingRuleSourceDetail
  {
    const copy: QualifyingRuleSourceDetail = {
      offerSetId: detail.offerSetId,
      accountId: detail.accountId,
      vehicle: this.copySourceVehicle(detail.vehicle),
      offers: this.copySourceOffers(detail.offers)
    };

    return copy;
  }

  private copySourceVehicle(vehicle: QualifyingRuleSourceVehicle): QualifyingRuleSourceVehicle
  {
    const copy: QualifyingRuleSourceVehicle = {
      title: vehicle.title,
      year: vehicle.year,
      make: vehicle.make,
      model: vehicle.model,
      trim: vehicle.trim,
      vin: vehicle.vin,
      stockNumber: vehicle.stockNumber,
      maximumPrice: vehicle.maximumPrice,
      newUsed: vehicle.newUsed,
      certified: vehicle.certified,
      visibilityScope: vehicle.visibilityScope,
      updatedAt: vehicle.updatedAt
    };

    return copy;
  }

  private copySourceOffers(offers: QualifyingRuleSourceOffers): QualifyingRuleSourceOffers
  {
    const copy: QualifyingRuleSourceOffers = {
      lease: this.copyLeaseOffer(offers.lease),
      alternateLease: this.copyLeaseOffer(offers.alternateLease),
      finance: this.copyFinanceOffer(offers.finance),
      promotionalApr: this.copyAprOffer(offers.promotionalApr)
    };

    return copy;
  }

  private copyLeaseOffer(offer: QualifyingRuleSourceLeaseOffer | null): QualifyingRuleSourceLeaseOffer | null
  {
    let copy: QualifyingRuleSourceLeaseOffer | null = null;
    if (offer !== null) {
      copy = {
        paymentAmount: offer.paymentAmount,
        moneyDown: offer.moneyDown,
        percentageDown: offer.percentageDown,
        term: offer.term,
        annualMileage: offer.annualMileage,
        additionalText: offer.additionalText
      };
    }

    return copy;
  }

  private copyFinanceOffer(offer: QualifyingRuleSourceFinanceOffer | null): QualifyingRuleSourceFinanceOffer | null
  {
    let copy: QualifyingRuleSourceFinanceOffer | null = null;
    if (offer !== null) {
      copy = {
        paymentAmount: offer.paymentAmount,
        moneyDown: offer.moneyDown,
        percentageDown: offer.percentageDown,
        apr: offer.apr,
        term: offer.term,
        additionalText: offer.additionalText
      };
    }

    return copy;
  }

  private copyAprOffer(offer: QualifyingRuleSourceAprOffer | null): QualifyingRuleSourceAprOffer | null
  {
    let copy: QualifyingRuleSourceAprOffer | null = null;
    if (offer !== null) {
      copy = {
        apr: offer.apr,
        term: offer.term,
        additionalText: offer.additionalText
      };
    }

    return copy;
  }

  private normalizeText(value: string | null): string | null
  {
    let normalizedValue: string | null = null;
    if (value !== null && value.trim() !== '') {
      normalizedValue = value.trim();
    }

    return normalizedValue;
  }

  private focusHeading(step: RuleWorkflowStep): void
  {
    setTimeout(() => {
      if (step === RuleWorkflowStep.RuleDetails) {
        this.stepOneHeading?.nativeElement.focus();
      }
      if (step === RuleWorkflowStep.OfferSetup) {
        this.stepTwoHeading?.nativeElement.focus();
      }
    }, focusDelayMilliseconds);
  }

  private focusDetailError(): void
  {
    setTimeout(() => this.detailErrorRegion?.nativeElement.focus(), focusDelayMilliseconds);
  }

  private handleModalHidden(): void
  {
    this.invalidateDetailRequest();
    this.activeStep = RuleWorkflowStep.RuleDetails;
  }

  private hasInitializationChange(changes: SimpleChanges): boolean
  {
    const opened = changes['visible'] !== undefined && this.visible;
    const inputChanged = changes['mode'] !== undefined
      || changes['accountId'] !== undefined
      || changes['rule'] !== undefined
      || changes['existingDraft'] !== undefined;
    const changed = opened || inputChanged;

    return changed;
  }
}
